import * as React from 'react';
import { View, StyleSheet, TouchableOpacity, TouchableWithoutFeedback, } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, { Extrapolation, interpolate, runOnJS, useAnimatedStyle, useDerivedValue, useSharedValue, Reanimated3DefaultSpringConfig, withSpring, withTiming, } from 'react-native-reanimated';
import { IconArrow, ModalHandle, rem, Spacer } from 'design-system';
import { useMarginBottom } from 'hooks/useMarginBottom';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import Text from 'design-system/Text';
import useColors from 'hooks/useColors';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useDisableAndroidBackButton from 'hooks/useDisableAndroidBackButton';
import { useAppSelector } from 'store/hooks';
import { selectTabsPriority } from 'reducers/selectors/organiseTabs';
import useIsTablet from 'hooks/useIsTablet';
import useBottomTabHeight from 'hooks/useBottomTabHeight';
import { isWeb } from '../constants';
const finalHeightWithoutMarginBottom = rem(314);
const initialHeight = rem(90);
const marginOutputRange = [rem(16), 0];
const velocityThreshold = 400;
const logoWidthOutputRange = [rem(50), rem(80)];
const logoMarginTopOutputRange = [0, rem(16)];
const logoMarginBottomOutputRange = [0, rem(24)];
/**
 * The component is a bar floating at the bottom, accepting an optional press handler.
 * It can be swiped up to expand, into a modal.
 *
 * TODO: This is immature. Its UI design is not very polished (especially on tablets).
 * Its implementation is decoupled from its current only usage but needs to be improved to be fully generic:
 * - no Web
 * - fixed height: could be made general with onLayout
 * - not considered: showing another modal on press
 */
export const ExpandableFloatingBarView = React.forwardRef(({ icon, title, description, onPress, isInTab, children }, ref) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const timeout = React.useRef(undefined);
    const { hideTabNavigation } = useAppSelector(selectTabsPriority);
    const isTablet = useIsTablet();
    const bottomTabBarHeight = useBottomTabHeight();
    const isAboveBottomTabBar = isInTab && !hideTabNavigation && !isTablet;
    const marginBottomWithoutTabBar = useMarginBottom('bottom');
    const marginBottom = React.useMemo(() => isAboveBottomTabBar
        ? { bottom: rem(8) + bottomTabBarHeight }
        : marginBottomWithoutTabBar, [isAboveBottomTabBar, bottomTabBarHeight, marginBottomWithoutTabBar]);
    // On web the bar sits in the content area beside the sidebar, so it uses that
    // column rather than the whole window
    const { paddingHorizontal, deviceWidth, contentWidth } = useAppFrameDimensions(isAboveBottomTabBar || isWeb);
    const expandedLeft = paddingHorizontal;
    const expandedRight = paddingHorizontal;
    const expandedWidth = (isWeb ? contentWidth : deviceWidth) - expandedLeft - expandedRight;
    const targetMarginLeft = expandedLeft + marginOutputRange[0];
    const targetMarginRight = expandedRight + marginOutputRange[0];
    const targetBottom = marginBottom.bottom;
    const { finalHeight, maxTranslateY, midpoint } = React.useMemo(() => {
        const finalHeight = finalHeightWithoutMarginBottom +
            marginBottomWithoutTabBar.bottom;
        const maxTranslateY = finalHeight - initialHeight;
        const midpoint = -maxTranslateY / 2.5;
        return { finalHeight, maxTranslateY, midpoint };
    }, [marginBottomWithoutTabBar.bottom]);
    const translateY = useSharedValue(0);
    const lastTranslateY = useSharedValue(0);
    const anchorLeft = useSharedValue(targetMarginLeft);
    const anchorRight = useSharedValue(targetMarginRight);
    const anchorBottom = useSharedValue(targetBottom);
    React.useEffect(() => {
        anchorLeft.value = withTiming(targetMarginLeft, { duration: 220 });
        anchorRight.value = withTiming(targetMarginRight, { duration: 220 });
        anchorBottom.value = withTiming(targetBottom, { duration: 220 });
    }, [
        anchorBottom,
        anchorLeft,
        anchorRight,
        targetBottom,
        targetMarginLeft,
        targetMarginRight,
    ]);
    const panGesture = Gesture.Pan()
        .activeOffsetY([-2, 2])
        .onStart(() => {
        lastTranslateY.value = Math.max(-maxTranslateY, Math.min(0, translateY.value));
    })
        .onUpdate((e) => {
        const value = lastTranslateY.value + e.translationY;
        translateY.value = Math.max(-maxTranslateY, Math.min(0, value));
    })
        .onEnd((e) => {
        let snapTo;
        // Strong velocity takes priority
        if (e.velocityY < -velocityThreshold) {
            // Swiping up fast -> open
            snapTo = -maxTranslateY;
        }
        else if (e.velocityY > velocityThreshold) {
            // Swiping down fast -> close
            snapTo = 0;
        }
        else {
            // Use position: if past midpoint, snap to open, else close
            snapTo = translateY.value < midpoint ? -maxTranslateY : 0;
        }
        translateY.value = withSpring(snapTo, {
            ...Reanimated3DefaultSpringConfig,
            velocity: e.velocityY,
            damping: 20,
            stiffness: 200,
        });
        lastTranslateY.value = snapTo;
    });
    const containerAnimatedStyles = useAnimatedStyle(() => ({
        height: interpolate(translateY.value, [0, -maxTranslateY], [initialHeight, finalHeight], Extrapolation.CLAMP),
        right: interpolate(translateY.value, [0, -maxTranslateY], [anchorRight.value, expandedRight], Extrapolation.CLAMP),
        left: interpolate(translateY.value, [0, -maxTranslateY], [anchorLeft.value, expandedLeft], Extrapolation.CLAMP),
        bottom: anchorBottom.value,
        ...(!isTablet && {
            bottom: interpolate(translateY.value, [-maxTranslateY + 100, -maxTranslateY], [anchorBottom.value, 0], Extrapolation.CLAMP),
            borderBottomLeftRadius: interpolate(translateY.value, [-maxTranslateY + 100, -maxTranslateY], marginOutputRange, Extrapolation.CLAMP),
            borderBottomRightRadius: interpolate(translateY.value, [-maxTranslateY + 100, -maxTranslateY], marginOutputRange, Extrapolation.CLAMP),
        }),
    }), [
        anchorBottom,
        anchorLeft,
        anchorRight,
        expandedLeft,
        expandedRight,
        finalHeight,
        maxTranslateY,
        translateY,
        isTablet,
    ]);
    const backgroundAnimatedStyles = useAnimatedStyle(() => ({
        opacity: interpolate(translateY.value, [0, -maxTranslateY], [0, 0.5], Extrapolation.CLAMP),
    }));
    const contentBackgroundAnimatedStyles = useAnimatedStyle(() => {
        const progress = interpolate(translateY.value, [0, -maxTranslateY], [0, 1], Extrapolation.CLAMP);
        return {
            opacity: interpolate(progress, [0, 0.5], [1, 0], Extrapolation.CLAMP),
        };
    });
    const headerLeftContainerAnimatedStyles = useAnimatedStyle(() => ({
        flex: interpolate(translateY.value, [0, -maxTranslateY], [0, 1], Extrapolation.CLAMP),
    }));
    const headerRightContainerAnimatedStyles = useAnimatedStyle(() => ({
        opacity: interpolate(translateY.value, [0, -30], [1, 0], Extrapolation.CLAMP),
    }));
    const bodyContainerAnimatedStyles = useAnimatedStyle(() => {
        const progress = interpolate(translateY.value, [0, -maxTranslateY], [0, 1], Extrapolation.CLAMP);
        return {
            opacity: interpolate(progress, [0.2, 0.6], [0, 1], Extrapolation.CLAMP),
        };
    });
    const logoAnimatedStyles = useAnimatedStyle(() => ({
        width: interpolate(translateY.value, [0, -maxTranslateY], logoWidthOutputRange, Extrapolation.CLAMP),
        marginTop: interpolate(translateY.value, [0, -maxTranslateY], logoMarginTopOutputRange, Extrapolation.CLAMP),
        marginBottom: interpolate(translateY.value, [0, -maxTranslateY], logoMarginBottomOutputRange, Extrapolation.CLAMP),
    }));
    const handleClose = React.useCallback(() => {
        translateY.value = withSpring(0, {
            ...Reanimated3DefaultSpringConfig,
            damping: 50,
            stiffness: 400,
            overshootClamping: true,
        });
        lastTranslateY.value = 0;
    }, [lastTranslateY, translateY]);
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [backgroundPointerEvents, setBackgroundPointerEvents] = React.useState('none');
    useDerivedValue(() => {
        runOnJS(setIsExpanded)(translateY.value !== 0);
        runOnJS(setBackgroundPointerEvents)(translateY.value <= -maxTranslateY + 50 ? 'auto' : 'none');
    }, [maxTranslateY, translateY]);
    const isExpandedRef = React.useRef(false);
    isExpandedRef.current = isExpanded;
    useDisableAndroidBackButton(React.useCallback(() => {
        if (isExpandedRef.current) {
            handleClose();
            return true;
        }
        return false;
    }, [handleClose]));
    React.useImperativeHandle(ref, () => ({
        collapse: () => {
            if (timeout.current) {
                clearTimeout(timeout.current);
            }
            timeout.current = setTimeout(() => {
                handleClose();
            }, 400);
        },
    }), [handleClose]);
    React.useEffect(() => () => {
        clearTimeout(timeout.current);
    }, []);
    const childrenStyle = React.useMemo(() => ({ width: expandedWidth, alignSelf: 'center' }), [expandedWidth]);
    return (<View style={styles.container} pointerEvents="box-none">
      <Animated.View style={[styles.background, backgroundAnimatedStyles]} pointerEvents={backgroundPointerEvents}>
        <TouchableOpacity style={styles.backgroundButton} onPress={handleClose}/>
      </Animated.View>
      <Animated.View style={[styles.contentContainer, containerAnimatedStyles]}>
        <Animated.View style={[styles.contentBackground, contentBackgroundAnimatedStyles]}/>
        <GestureDetector gesture={panGesture}>
          <TouchableWithoutFeedback onPress={onPress} disabled={isExpanded || !onPress}>
            <View>
              <ModalHandle style={styles.modalHandleContainer} color={isExpanded ? undefined : colors.background.transparent}/>
              <View style={styles.headerContainer}>
                <Spacer w={16}/>
                <Animated.View style={[
            styles.headerLeftContainer,
            headerLeftContainerAnimatedStyles,
        ]}/>
                <Animated.View style={[styles.logoContainer, logoAnimatedStyles]}>
                  {icon}
                </Animated.View>
                <Animated.View style={[
            styles.headerRightContainer,
            headerRightContainerAnimatedStyles,
        ]}>
                  <Spacer w={16}/>
                  <View style={styles.headerRightContentContainer}>
                    <Text Text-16 color={colors.text.whiteOnColor} numberOfLines={1}>
                      {title}
                    </Text>
                    <Text TextThin-14 color={colors.text.whiteOnColor} numberOfLines={1}>
                      {description}
                    </Text>
                  </View>
                  {onPress !== undefined && (<TouchableOpacity onPress={onPress} style={styles.headerRightButton}>
                      <IconArrow direction={'right'} color={colors.text.whiteOnColor}/>
                    </TouchableOpacity>)}
                </Animated.View>
              </View>
              <Animated.View style={bodyContainerAnimatedStyles}>
                <View style={childrenStyle}>{children}</View>
              </Animated.View>
            </View>
          </TouchableWithoutFeedback>
        </GestureDetector>
      </Animated.View>
    </View>);
});
ExpandableFloatingBarView.displayName = 'ExpandableFloatingBarView';
export const ExpandableFloatingBarSpacer = React.memo(({ isInTab }) => {
    const { hideTabNavigation } = useAppSelector(selectTabsPriority);
    const isTablet = useIsTablet();
    const isAboveBottomTabBar = isInTab && !hideTabNavigation && !isTablet;
    const marginBottomWithoutTabBar = useMarginBottom('bottom');
    return (<View style={{
            height: initialHeight +
                (isAboveBottomTabBar
                    ? rem(8)
                    : marginBottomWithoutTabBar.bottom),
        }}/>);
});
const styleSet = createStyleSheets((colors) => ({
    container: {
        ...StyleSheet.absoluteFillObject,
    },
    background: {
        flex: 1,
        backgroundColor: '#000000',
        opacity: 0,
    },
    contentContainer: {
        bottom: 0,
        right: rem(16),
        left: rem(16),
        overflow: 'hidden',
        position: 'absolute',
        height: initialHeight,
        borderTopRightRadius: rem(16),
        borderTopLeftRadius: rem(16),
        borderBottomRightRadius: rem(16),
        borderBottomLeftRadius: rem(16),
        backgroundColor: colors.cards.onDark,
    },
    modalHandleContainer: {
        height: rem(24),
        alignItems: 'center',
        justifyContent: 'center',
    },
    backgroundButton: {
        flex: 1,
    },
    contentBackground: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: colors.background.contrast,
    },
    headerContainer: {
        flexDirection: 'row',
    },
    logoContainer: {
        width: rem(50),
        aspectRatio: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    headerRightContainer: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
    },
    headerLeftContainer: {
        flex: 0,
    },
    headerRightContentContainer: {
        flex: 1,
        justifyContent: 'center',
    },
    headerRightButton: {
        padding: rem(16),
    },
}));
